import React, { useEffect, useMemo, useState } from 'react';

import gql from 'graphql-tag';

import {
  GetAdyenPayments,
  GetStores,
  GetAdyenPayments_operativeViewer_balanceLegalEntity_balanceAccounts_transactions as Transaction,
} from './__queries__';
import TransactionTable from './Components/TransactionTable';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useButton, useDates, useI18n, useLogger } from '~Hooks';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';
import DateInput from '~ComponentsImproved/DateInput';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { Box, Button, Input, WhiteButton } from '~Styles';
import FromToInput from '~ComponentsImproved/FromToInput';
import { CircleSpinner } from 'react-spinners-kit';

const DateInputWrapper = styled.div`
  position: relative;
  width: 300px;

  > div {
    height: 100%;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 230px;
`;

const ContentWrapper = styled.div`
  height: calc(100vh - 128px);
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
`;

const ClearLink = styled.span`
  color: var(--colorUIBlue);
  cursor: pointer;
  text-decoration: underline;
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;
`;

const ReloadWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const ReloadButton = styled(WhiteButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 130px;
`;

const STORE_QUERY = gql`
  query GetStores {
    operativeViewer {
      id
      adyenStore {
        id
        name
        reference
      }
    }
  }
`;

const TRANSACTIONS_QUERY = gql`
  query GetAdyenPayments(
    $amount_gt: Int
    $amount_lt: Int
    $createdAt_gt: String
    $createdAt_lt: String
    $identifier: String
  ) {
    operativeViewer {
      id
      adyenPayments(createdAt_gt: $createdAt_gt, createdAt_lt: $createdAt_lt) {
        id
        createdAt
        pspReference
        amount
        methodIdentifier
        status
        origin
      }

      balanceLegalEntity {
        id

        balanceAccounts {
          id
          transactions(
            amount_gt: $amount_gt
            amount_lt: $amount_lt
            createdAt_gt: $createdAt_gt
            createdAt_lt: $createdAt_lt
            identifier: $identifier
          ) {
            id
            pspReference
            amount
            currency
            created_at
            type
            reference
            fee_amount
            paymentMethod
            methodIdentifier
            store_id
          }
        }
      }
    }
  }
`;

const Transactions: React.FC = () => {
  const { i18n } = useI18n();
  const dates = useDates();
  const logger = useLogger('adyen-transactions');

  const [amountRange, setAmountRange] = useState<{
    from: number | null;
    to: number | null;
  }>({ from: null, to: null });

  const defaultStartDate = DateTime.now()
    .startOf('day')
    .minus({ days: 7 })
    .plus({ hours: 5 })
    .toJSDate();
  const defaultEndDate = DateTime.now()
    .endOf('day')
    .toJSDate();

  const [selectedDateRange, setSelectedDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: defaultStartDate,
    to: defaultEndDate,
  });
  const [reloading, setReloading] = useState<boolean>(false);
  const [methodIdentifier, setMethodIdentifier] = useState<string>('');

  const storeQuery = useQuery<GetStores>(STORE_QUERY);
  const store = storeQuery.data?.operativeViewer?.adyenStore || null;

  const transactionsQuery = useQuery<GetAdyenPayments>(TRANSACTIONS_QUERY, {
    variables: {
      amount_gt: (amountRange.from || 0) * 100,
      amount_lt: (amountRange.to || 0) * 100,
      createdAt_gt: selectedDateRange.from
        ? dates.getAccountingStartDate(new Date(selectedDateRange.from))
        : '',
      createdAt_lt: selectedDateRange.to
        ? dates.getAccountingEndDate(new Date(selectedDateRange.to))
        : '',
      identifier: methodIdentifier,
    },
    skip: !store,
    pollInterval: 10000,
    notifyOnNetworkStatusChange: true,
  });

  const legalEntity = transactionsQuery.data?.operativeViewer?.balanceLegalEntity || null;
  const adyenPayments = transactionsQuery.data?.operativeViewer?.adyenPayments || [];

  const checkIfAnyFilterIsApplied = () => {
    return amountRange.from !== null || amountRange.to !== null || methodIdentifier !== '';
  };

  useEffect(() => {
    if (transactionsQuery.error) {
      logger.error('Error fetching transactions', {
        queryError: transactionsQuery.error,
        variables: transactionsQuery.variables,
        storeId: store?.id,
        storeReference: store?.reference,
      });
    }
  }, [transactionsQuery]);

  const clearFilters = () => {
    setAmountRange({ from: null, to: null });
    setMethodIdentifier('');
    setSelectedDateRange({
      from: defaultStartDate,
      to: defaultEndDate,
    });
  };

  const filteredAndMergedTransactions = useMemo(() => {
    let txs = legalEntity?.balanceAccounts[0].transactions || [];
    txs = txs
      .filter((tx) => ['purchase', 'refund', 'refusal'].includes(tx.type))
      .filter((tx) => tx.store_id === store?.reference);

    const merged = [...txs];
    for (const payment of adyenPayments) {
      const exists = txs.find((tx) => tx.pspReference === payment.pspReference);

      // match your rules
      const methodMatches = !methodIdentifier || payment.methodIdentifier === methodIdentifier;
      const amountInRange =
        (!amountRange.from || payment.amount >= amountRange.from * 100) &&
        (!amountRange.to || payment.amount <= amountRange.to * 100);

      if (!exists && methodMatches && amountInRange && payment.status === 'AUTHORIZED') {
        merged.push({
          pspReference: payment.pspReference,
          amount: payment.amount,
          currency: 'SEK',
          fee_amount: 0,
          id: payment.id,
          reference: '',
          // @ts-ignore
          __typename: '',
          created_at: payment.createdAt,
          methodIdentifier: payment.methodIdentifier,
          paymentMethod: '123',
          store_id: '',
          type: payment.origin === 'APP' ? 'online' : 'incoming',
        });
      }
    }

    return merged.sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
  }, [legalEntity, adyenPayments, store, amountRange, methodIdentifier]);

  const reloadButton = useButton({
    onClick: () => {
      // To instantly show the loading spinner
      setReloading(true);
      setTimeout(() => setReloading(false), 1000);
      transactionsQuery.refetch();
    },
    disabled: transactionsQuery.loading || reloading,
  });

  return (
    <>
      <BackTopBar title={i18n('Menu.PaymentsRefunds')} />
      <ContentWrapper>
        <Box display="flex" gap="small" marginBottom="small">
          <DateInputWrapper>
            <DateInput
              selectedDate={[selectedDateRange.from, selectedDateRange.to]}
              selectRange={true}
              setSelectedDate={(date) => {
                if (!date) {
                  setSelectedDateRange({
                    from: defaultStartDate,
                    to: defaultEndDate,
                  });
                  return;
                }

                if (date instanceof Array && date.length === 2) {
                  setSelectedDateRange({
                    from: date[0] || defaultStartDate,
                    to: date[1] || defaultEndDate,
                  });
                }
              }}
              maxDate={new Date()}
            />
          </DateInputWrapper>

          <SearchWrapper>
            <Input
              type="number"
              value={methodIdentifier}
              min={0}
              max={9999}
              as="input"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setMethodIdentifier(e.target.value);
              }}
              placeholder={i18n('Transactions.MethodIdentifier.Search')}
            />
          </SearchWrapper>

          <FromToInput amountRange={amountRange} setAmountRange={setAmountRange} />

          {checkIfAnyFilterIsApplied() && (
            <Box onClick={clearFilters} display="flex" alignItems="center">
              <ClearLink>{i18n('Transactions.ClearFilter')}</ClearLink>
            </Box>
          )}
          <ReloadWrapper>
            <ReloadButton {...reloadButton} size="small">
              {i18n('Transactions.Reload')}
              {transactionsQuery.loading && (
                <CircleSpinner color="var(--colorBlueGray1200)" size={12} />
              )}
            </ReloadButton>
          </ReloadWrapper>
        </Box>

        {!transactionsQuery.data && transactionsQuery.loading ? (
          <CircleSpinner color="var(--colorBlueGray1200)" size={12} />
        ) : (
          <TransactionTable transactions={filteredAndMergedTransactions} />
        )}
      </ContentWrapper>
    </>
  );
};

export default Transactions;
